import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../service";

// Bulk Valuation OAuth integrations (WAT-I2726). Provider-list-driven, not
// hardcoded to Transact: the list itself comes from masterdata's
// GET providers endpoint, which only returns providers that have a
// registered OAuth strategy server-side — adding a future provider there is
// enough for it to appear here automatically, no UI change needed.
//
// Connect's OAuth flow is entirely tenant-level (no per-consultant account
// selection) and the callback is handled server-side by masterdata directly
// — there is no frontend callback route or exchangeToken thunk to mirror
// CashCalc's; completion is detected purely by polling getStatus (see
// TransactPage.tsx).
export interface BvProvider {
  code: string;
  name: string;
  status: string; // NOT_CONNECTED | AUTHORISATION_PENDING | CONNECTED | REAUTHORISATION_REQUIRED | DISCONNECTED | ERROR
  lastConnected: string | null;
  expiryDate: string | null;
  firms: any[];
}

interface TransactState {
  providers: BvProvider[];
  listLoading: boolean;
}

const initialState: TransactState = {
  providers: [],
  listLoading: false,
};

export const fetchBvProviders = createAsyncThunk(
  "transact/fetchProviders",
  async (_: void, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.transact.listProviders();
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || "Failed to load integration providers");
      }
      return (response.data?.data || []) as BvProvider[];
    } catch (error: any) {
      // An outage (masterdata down, 401/500) must surface as an error, not
      // a silent empty list that reads as "no providers configured".
      return rejectWithValue(error.message || "Failed to load integration providers");
    }
  }
);

export const checkProviderStatus = createAsyncThunk(
  "transact/checkStatus",
  async (providerCode: string, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.transact.getStatus(providerCode);
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || "Failed to check connection status");
      }
      const data = response.data?.data || response.data || {};
      return {
        code: providerCode,
        status: data?.status || "NOT_CONNECTED",
        lastConnected: data?.lastConnected || null,
        expiryDate: data?.expiryDate || null,
        firms: data?.firms || [],
      };
    } catch (error: any) {
      // A blip (network error, 5xx) during polling must not be treated as
      // "confirmed not connected" — that overwrote a genuinely CONNECTED row.
      // Rejecting (with no .rejected case touching `providers`) leaves the
      // existing row exactly as it was.
      return rejectWithValue(error.message || "Failed to check connection status");
    }
  }
);

export const getProviderAuthorizeUrl = createAsyncThunk(
  "transact/getAuthorizeUrl",
  async (providerCode: string, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.transact.connect(providerCode);
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || "Failed to start the connection");
      }
      return response.data?.data?.authorizeUrl || response.data?.authorizeUrl || null;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to start the connection");
    }
  }
);

export const disconnectProvider = createAsyncThunk(
  "transact/disconnect",
  async (providerCode: string, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.transact.disconnect(providerCode);
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || "Failed to disconnect");
      }
      return providerCode;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to disconnect");
    }
  }
);

export const transactSlice = createSlice({
  name: "transact",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBvProviders.pending, (state) => {
        state.listLoading = true;
      })
      .addCase(fetchBvProviders.fulfilled, (state, action) => {
        state.listLoading = false;
        state.providers = action.payload;
      })
      .addCase(fetchBvProviders.rejected, (state) => {
        state.listLoading = false;
      })
      .addCase(checkProviderStatus.fulfilled, (state, action) => {
        const provider = state.providers.find((p) => p.code === action.payload.code);
        if (provider) {
          provider.status = action.payload.status;
          provider.lastConnected = action.payload.lastConnected;
          provider.expiryDate = action.payload.expiryDate;
          provider.firms = action.payload.firms;
        }
      })
      .addCase(disconnectProvider.fulfilled, (state, action) => {
        const provider = state.providers.find((p) => p.code === action.payload);
        if (provider) {
          provider.status = "NOT_CONNECTED";
          provider.lastConnected = null;
          provider.expiryDate = null;
          provider.firms = [];
        }
      });
  },
});

export default transactSlice.reducer;
